import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

/** Página normalizada que debe devolver `searchFn` (ya mapeada desde Spring/Crm page). */
export interface EntitySelectPage<T> {
  items: T[];
  totalPages: number;
}

/**
 * Buscador-selector genérico por nombre/correo (typeahead) para formularios del CRM.
 *
 * El usuario nunca escribe un ID: escribe texto, ve resultados con nombre + dato secundario
 * (correo / razón social…) y elige uno. El componente emite el `id` por `valueChange`.
 *
 * Diseñado para reutilizarse con CUALQUIER entidad: el padre inyecta la función de búsqueda
 * (`searchFn`, server-side o client-side) y cómo formatear cada item (`labelFn`/`sublabelFn`).
 *
 * IMPORTANTE: pase `searchFn`/`labelFn`/`sublabelFn` como ARROW FUNCTIONS del componente padre
 * (campos de clase con `=>`), porque el componente las invoca como `this.searchFn(...)` y de lo
 * contrario se pierde el `this` del padre.
 *
 * INC_CRM_020 (7) inc23/inc25 — este componente usa estrategia de detección por DEFECTO, pero
 * varios padres (el inspector del constructor de workflows y todos sus paneles de configuración)
 * son `OnPush`. Cuando un ancestro `OnPush` no está marcado como sucio, Angular se salta TODO su
 * subárbol en el `tick()`, así que el resultado de la búsqueda llegaba pero nunca se repintaba:
 * el spinner se quedaba girando «indefinidamente» y las opciones solo aparecían al hacer clic
 * fuera (ese evento sí marcaba la vista). Por eso cada callback asíncrono termina en
 * `cdr.markForCheck()`, que marca este componente y toda su cadena de ancestros.
 */
@Component({
  selector: 'app-entity-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './entity-select.component.html',
})
export class EntitySelectComponent<T = unknown> implements OnInit, OnChanges, OnDestroy {
  /** Placeholder del input. */
  @Input() placeholder = 'Buscar por nombre o correo…';
  /** ID seleccionado actualmente (o null). */
  @Input() value: number | null = null;
  /** Texto a mostrar cuando el padre fija un `value` externamente (ej. al editar). */
  @Input() displayText = '';
  /** Mostrar la "X" para limpiar la selección. */
  @Input() allowClear = true;
  /** Deshabilitar el control. */
  @Input() disabled = false;
  /** Listar resultados al enfocar/Enter aunque el texto esté vacío. */
  @Input() openOnFocus = true;
  /** Mínimo de caracteres para disparar la búsqueda (0 = busca también vacío → lista todo). */
  @Input() minChars = 0;

  /** Función de búsqueda: (texto, página) → página normalizada de items. */
  @Input() searchFn!: (term: string, page: number) => Observable<EntitySelectPage<T>>;
  /** Texto principal de cada item (nombre). */
  @Input() labelFn: (item: T) => string = (i: unknown) => String((i as { name?: unknown })?.name ?? '');
  /** Texto secundario de cada item (correo, etc.). Vacío = no se muestra. */
  @Input() sublabelFn: (item: T) => string = () => '';
  /** ID de cada item. */
  @Input() idFn: (item: T) => number = (i: unknown) => (i as { id: number }).id;

  @Output() valueChange = new EventEmitter<number | null>();
  @Output() selected = new EventEmitter<T | null>();

  term = '';
  results: T[] = [];
  showDropdown = false;
  searching = false;
  loadingMore = false;
  page = 0;
  totalPages = 0;
  /** La última búsqueda terminó sin resultados: se dice en el desplegable en vez de no abrirlo. */
  noResults = false;

  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  /** Término de la última búsqueda ya resuelta, para reabrir sin repetirla al volver a enfocar. */
  private lastSearchedTerm: string | null = null;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.term = this.displayText || '';

    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.page = 0;
          this.results = [];
          this.totalPages = 0;
          this.noResults = false;
          if (this.minChars > 0 && term.trim().length < this.minChars) {
            this.showDropdown = false;
            this.searching = false;
            this.cdr.markForCheck();
            return of(null);
          }
          this.searching = true;
          this.cdr.markForCheck();
          return this.runSearch(term, 0);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.searching = false;
        if (res) {
          this.applyResults(res);
        }
        this.cdr.markForCheck();
      });
  }

  /** Vuelca una página de resultados y decide si el desplegable se abre y con qué mensaje. */
  private applyResults(res: EntitySelectPage<T>): void {
    this.results = res.items;
    this.totalPages = res.totalPages;
    this.lastSearchedTerm = this.term.trim();
    this.noResults = this.results.length === 0;
    this.showDropdown = true;
  }

  ngOnChanges(c: SimpleChanges): void {
    // El padre resolvió/cambió el texto a mostrar (ej. al cargar para editar), o lo limpió.
    if (c['displayText'] && !this.showDropdown) {
      if (this.displayText) {
        this.term = this.displayText;
      } else if (this.value === null) {
        // El padre limpió la selección externamente (p. ej. al cambiar de negocio) → vaciar el input visible.
        this.term = '';
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private runSearch(term: string, page: number): Observable<EntitySelectPage<T>> {
    return this.searchFn(term.trim(), page).pipe(
      catchError(() => of({ items: [] as T[], totalPages: 0 }))
    );
  }

  onInput(term: string): void {
    this.term = term;
    if (this.value !== null) {
      this.value = null;
      this.valueChange.emit(null);
      this.selected.emit(null);
    }
    this.search$.next(term);
  }

  onEnter(event: Event): void {
    event.preventDefault();
    this.runFreshSearch();
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMore) return;
    if (this.page + 1 >= this.totalPages) return;

    this.loadingMore = true;
    this.page++;
    this.cdr.markForCheck();
    this.runSearch(this.term, this.page)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.loadingMore = false;
        this.results = [...this.results, ...res.items];
        this.totalPages = res.totalPages;
        this.cdr.markForCheck();
      });
  }

  /**
   * Búsqueda inmediata (Enter / re-enfoque), fuera del `Subject`: éste lleva
   * `distinctUntilChanged()`, así que volver al campo con el MISMO texto —el caso normal, con el
   * texto vacío— quedaba silenciosamente descartado y el desplegable no volvía a abrirse.
   */
  private runFreshSearch(): void {
    this.page = 0;
    this.results = [];
    this.totalPages = 0;
    this.noResults = false;
    this.searching = true;
    this.showDropdown = false;
    this.cdr.markForCheck();
    this.runSearch(this.term, 0)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.searching = false;
        this.applyResults(res);
        this.cdr.markForCheck();
      });
  }

  onFocus(): void {
    if (!this.openOnFocus || this.value !== null) return;
    if (this.minChars > 0 && this.term.trim().length < this.minChars) return;
    // Ya buscado este mismo texto: se reabre lo que hay, sin volver a llamar al API.
    if (this.lastSearchedTerm === this.term.trim() && !this.searching) {
      this.showDropdown = true;
      this.cdr.markForCheck();
      return;
    }
    this.runFreshSearch();
  }

  pick(item: T): void {
    this.value = this.idFn(item);
    this.term = this.labelFn(item);
    this.showDropdown = false;
    this.noResults = false;
    this.results = [];
    this.lastSearchedTerm = null;
    this.valueChange.emit(this.value);
    this.selected.emit(item);
  }

  clear(): void {
    this.value = null;
    this.term = '';
    this.results = [];
    this.showDropdown = false;
    this.noResults = false;
    this.lastSearchedTerm = null;
    this.valueChange.emit(null);
    this.selected.emit(null);
  }

  close(): void {
    // Delay para permitir que el (mousedown) del item se dispare antes del blur.
    setTimeout(() => {
      this.showDropdown = false;
      this.cdr.markForCheck();
    }, 200);
  }
}
